import React from 'react';
import { Link } from 'react-router-dom';
import BlockRenderer from './layouts/BlockRenderer';
import LayoutRenderer from './layouts/LayoutRenderer';
import { BlockSkinContext } from './layouts/ModernBlocks';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernPage — the «Moderno» standard for Page Builder pages (s140).
// Same page data (layout, zones, banner_image, summary, content); only the
// presentation changes: a standard page header (the banner the CMS already
// stores), Split Screen read as article + side column, and the blocks in the
// «Moderno» skin (ModernBlocks via BlockSkinContext). Other layouts keep their
// own grid under the new header.
// Approved by Anthony (s140): banner header + article/side column at 7/5
// (the side column stays in view while scrolling on desktop).
// (no flag = the current page).
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);

const CSS = `
.mip-prose{font-size:1.0625rem;line-height:1.78;color:var(--color-body-text,#475569);overflow-wrap:break-word}
.mip-prose p{margin:0 0 1.1em}
.mip-prose .ql-align-justify,.mip-prose .ql-align-center{text-align:left}
.mip-prose strong,.mip-prose b{color:var(--color-heading,#1a2332)}
.mip-prose a{color:var(--accent-strong,#b86500);text-decoration:underline;text-underline-offset:.3em}
.mip-prose h1,.mip-prose h2,.mip-prose h3,.mip-prose h4{font-family:${SERIF};font-weight:600;color:var(--color-heading,#1a2332)!important;line-height:1.25;margin:1.6em 0 .5em}
.mip-prose h1,.mip-prose h2{font-size:1.6rem}
.mip-prose h3,.mip-prose h4{font-size:1.25rem}
.mip-prose>*:first-child,.mip-prose>*:first-child>*:first-child{margin-top:0}
.mip-prose ul,.mip-prose ol{padding-left:1.2em;margin:0 0 1.1em}
.mip-prose ul{list-style:disc}
.mip-prose ol{list-style:decimal}
.mip-prose ul ul{list-style:circle}
.mip-prose li{margin:.25em 0}
.mip-prose li>ul,.mip-prose li>ol{margin:.25em 0 .5em}
.mip-prose ul li::marker{color:var(--color-accent,#ff9c1b)}
.mip-prose img{max-width:100%;border-radius:3px}
.mip-stack>.mip-prose{max-width:72ch}
.mip-stack>figure,.mip-stack>a:has(>figure){max-width:896px}
`;

function Zone({ blocks, className = 'flex flex-col gap-8' }) {
  const sorted = [...(blocks || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  if (!sorted.length) return null;
  return <div className={className}>{sorted.map(b => <BlockRenderer key={b.id} block={b} />)}</div>;
}

// Breadcrumb trail: [{ label, to }] — the last item is the current page.
function Crumbs({ trail }) {
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-[13px] font-semibold" style={{ color: 'rgba(255,255,255,.7)' }}>
      {trail.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span aria-hidden="true">/</span>}
          {c.to && i < trail.length - 1
            ? <Link to={c.to} className="mx-link mx-dark" style={{ color: '#fff' }}>{c.label}</Link>
            : <span className="truncate">{c.label}</span>}
        </React.Fragment>
      ))}
    </nav>
  );
}

/* ── Header: the «Moderno» page hero, shared by Page Builder pages, /gallery
   and the album pages. banner = image path (graphite when empty). ───────── */
export function PageHero({ title, summary, banner, trail, eyebrow, badge }) {
  const [broken, setBroken] = React.useState(false);
  const src = broken ? null : img(banner);
  return (
    <header className="relative isolate overflow-hidden min-h-[320px] lg:min-h-[400px] flex items-end" style={{ background: `var(--op-dark, ${GRAPHITE})` }}>
      {src && <img src={src} alt="" onError={() => setBroken(true)} className="absolute inset-0 -z-10 w-full h-full object-cover" />}
      <span className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(90deg, rgba(10,11,14,.9) 0%, rgba(10,11,14,.6) 55%, rgba(10,11,14,.25) 100%)' }} />
      <div className={`${shell} w-full pt-32 pb-12 lg:pb-14 flex flex-col gap-4`}>
        <Crumbs trail={trail || [{ label: 'Home', to: '/' }, { label: title }]} />
        {badge}
        {eyebrow && <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.18em', color: 'var(--color-accent, #ff9c1b)' }}>{eyebrow}</span>}
        <h1 className="font-bold text-white max-w-3xl" style={{ fontFamily: `var(--op-head, ${SERIF})`, fontSize: 'clamp(2.2rem, 4.4vw, 3.6rem)', lineHeight: 1.06, textWrap: 'balance' }} data-testid="layout-page-title">{title}</h1>
        {summary && <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">{summary}</p>}
      </div>
    </header>
  );
}

function BannerHeader({ page }) {
  return <PageHero title={page.title} summary={page.summary} banner={page.banner_image} />;
}

/* ── Body: Split Screen = article (7) + side column (5) ────────────────── */
function SplitBody({ zones }) {
  const hasAside = (zones.right || []).length > 0;
  return (
    <div className={`${shell} py-14 lg:py-20 grid gap-12 lg:gap-16 ${hasAside ? 'lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]' : ''}`}>
      <Zone blocks={zones.left} className="flex flex-col gap-8 min-w-0 max-w-[72ch]" />
      {hasAside && <aside className="flex flex-col gap-8 self-start lg:sticky lg:top-28"><Zone blocks={zones.right} /></aside>}
    </div>
  );
}

/* ── Body: single-column layouts (Boxed / Full Width / List) ─────────────
   Blocks stack on the standard 1440 shell; running text keeps a reading
   width, visual blocks (album cards, maps, service cards) use the full row. */
const STACKED = ['boxed', 'full_width', 'list'];
function StackBody({ zones }) {
  return (
    <div className={`${shell} py-14 lg:py-20`}>
      <Zone blocks={zones.main} className="mip-stack flex flex-col gap-10" />
    </div>
  );
}

export default function ModernPage({ page }) {
  const zones = page.zones || {};
  let body;
  if (page.layout === 'split_screen') body = <SplitBody zones={zones} />;
  else if (STACKED.includes(page.layout)) body = <StackBody zones={zones} />;
  else if (page.layout) body = <LayoutRenderer page={page} hasHero hideTitle />;
  else body = (
    <div className={`${shell} py-14 lg:py-20`}>
      {page.content && <div className="mip-prose max-w-[72ch]" dangerouslySetInnerHTML={{ __html: page.content }} />}
    </div>
  );
  return (
    <BlockSkinContext.Provider value="modern">
      <div data-testid="dynamic-page" className="bg-white">
        <style>{CSS}</style>
        <BannerHeader page={page} />
        {body}
      </div>
    </BlockSkinContext.Provider>
  );
}
